/**
    @title: Lazy Loading
    @observers: {
        - image: Images with srcset data attribute
        - iframe: Iframes with the iframe src data attribute
        - background: backgrounds with the bg data attribute
    }
**/

// Set these variables before the functions so they can be used in multiple places
let lazyImageObserver
let lazyIframeObserver
let lazyBackgroundObserver

/**
    @title: Lazy
    @description: Uses Intersection Observer to check whether or not the item is within the viewport.
		If it is then we need to change the markup so the element is loaded.
**/
function lazy() {
	// We want to create arrays from the results so we can loop through all node items
	const lazyImages = Array.from(document.querySelectorAll("[data-srcset]"))
	const lazyIframes = Array.from(document.querySelectorAll("[data-iframe-src]"))
	const lazyBackgrounds = Array.from(document.querySelectorAll("[data-bg]"))

	// First we need to check that there is browser support for IntersectionObserver
	if ("IntersectionObserver" in window) {
		// Image Observer for loading Images
		lazyImageObserver = new IntersectionObserver(function (images) {
			// Loop through all applicable images on the page
			images.forEach(function (image) {
				// Returns a boolean value if the image intersects with the intersection observer's root
				if (image.isIntersecting) {
					// Get the target image
					let target = image.target

					// Set the target src - needed for any browser which wouldn't accept the srcset
					if (target.dataset.src) {
						target.src = target.dataset.src
					}

					// Set the target srcset from the data attribute
					target.srcset = target.dataset.srcset ? target.dataset.srcset : ""

					// Set the target sizes from the data attribute
					target.sizes = target.dataset.sizes ? target.dataset.sizes : ""

					// Remove the opacity class from the target so it will fade in nicely
					target.classList.remove("opacity-0")

					// Once the image has loaded in, we will add a class to show this, and unobserve so the JS doesn't run again on this item
					target.onload = () => {
						target.classList.add("lazy-loaded")
						lazyImageObserver.unobserve(target)
					}
				}
			})
		})

		// Loop through all the images found and run the observer above
		lazyImages.forEach(function (images) {
			lazyImageObserver.observe(images)
		})

		// Iframe Observer for loading iframes
		lazyIframeObserver = new IntersectionObserver(function (iframes) {
			// Loop through all applicable iframes on the page
			iframes.forEach(function (iframe) {
				// Returns a boolean value if the iframe intersects with the intersection observer's root
				if (iframe.isIntersecting) {
					// Get the target iframe
					let target = iframe.target

					// Set the target src - This should then load any external scripts needed. e.g Youtube or Google Maps
					target.src = target.dataset.iframeSrc

					// Once the iframe has loaded in, we will unobserve so the JS doesn't run again on this item
					target.onload = () => {
						lazyImageObserver.unobserve(target)
					}
				}
			})
		})

		// Loop through all the iframes found and run the observer above
		lazyIframes.forEach(function (iframes) {
			lazyIframeObserver.observe(iframes)
		})

		// Background Observer for loading background
		lazyBackgroundObserver = new IntersectionObserver(function (backgrounds) {
			// Loop through all applicable backgrounds on the page
			backgrounds.forEach(function (background) {
				// Returns a boolean value if the iframe intersects with the intersection observer's root
				if (background.isIntersecting) {
					// Get the target container
					let target = background.target

					// Set the target style - This will only set the background image. All styles should already be applied to the container
					target.style.backgroundImage = 'url("' + target.dataset.bg + '")'

					// Once changes we can unobserve. This is not onload due to the image already being loaded by the dataset
					lazyBackgroundObserver.unobserve(target)
				}
			})
		})

		// Loop through all the backgrounds found and run the observer above
		lazyBackgrounds.forEach(function (backgrounds) {
			lazyBackgroundObserver.observe(backgrounds)
		})
	} else {
		// If there is no browser support, we want to switch the attributes on all instantly so the site looks correct
		lazyImages.forEach(function (image) {
			image.src = lazyImage.dataset.src
		})

		lazyIframes.forEach(function (iframe) {
			iframe.src = iframe.dataset.iframeSrc
		})

		lazyBackgrounds.forEach(function (background) {
			background.style.backgroundImage = 'url("' + background.dataset.bg + '")'
		})
	}
}

export { lazy }
